'use strict';

const IMAGE_REFERENCE = /(?:תמונ(?:ה|ות)|לוגו|צילום|תצלום|image|images|photo|photos|logo)/iu;
const HISTORICAL_REFERENCE = /(?:ששלחתי|שצירפתי|למעלה|קודמ(?:ת|ות)|אחרונ(?:ה|ות)|ראשונ(?:ה|ות)|שני(?:ה|יה|ות)|שליש(?:ית|יות)|לפני|above|earlier|previous|last|first|second|third)/iu;
// Live report 2026-10-15: "הוסף את התמונה לאתר" (add THE image to the site) after attaching an
// image sent no image at all, while "צרף את התמונה..." happened to work. A definite reference
// ("התמונה", "הלוגו", "the image", "this photo") points at an image the customer already gave,
// so it resolves exactly like "the image I sent". An indefinite "הוסף תמונה של חוף" does not.
const DEFINITE_REFERENCE = /(?:^|[^\p{L}])(?:ו|ש|ב|ל|מ|כ)?ה(?:תמונ(?:ה|ות)|לוגו|צילום|תצלום|צילומים)(?![\p{L}])|\b(?:the|this|that|these|those|my)\s+(?:image|images|photo|photos|picture|pictures|logo)\b/iu;
const PLURAL_DEFINITE = /(?:^|[^\p{L}])(?:ו|ש|ב|ל|מ|כ)?ה(?:תמונות|צילומים)(?![\p{L}])|\b(?:the|these|those|my)\s+(?:images|photos|pictures)\b/iu;
// Live report 2026-10-19 (watch store, build #7): "בדוק את בורר התמונות באדמין תראה מדוע אני לא
// מצליח להטמיע תמונה דרך הגלריה" was answered locally with "no confirmed image in this chat" and
// never reached the agent, because "התמונות" in "בורר התמונות" looked like a reference to an
// attached image. A request about the image MECHANISM of the product (its picker, gallery,
// upload, admin or code) is engineering work, not a reference to a chat attachment.
// Deliberately narrow: "שים את התמונה באדמין" (after attaching one) is still an embed request.
const IMAGE_FEATURE_WORK = /(?:בורר|גלרי|העלאת|רכיב|קוד|באג|תקלה|לא\s+מצליח|לא\s+עוב|מדוע|למה|תחקור|חקור|לחקור|picker|gallery|component|source\s+code|bug|debug|why|not\s+working)/iu;
// An explicit pointer to something the customer sent in this chat ("ששלחתי", "שצירפתי", "above").
const EXPLICIT_CHAT_REFERENCE = /(?:ששלחתי|שצירפתי|שהעליתי|שהעלתי|למעלה|בצ['׳]?אט|above|i\s+(?:sent|attached|uploaded))/iu;
const GENERIC_WORDS = new Set(['תמונה','תמונות','הלוגו','לוגו','צילום','התמונה','התמונות','ששלחתי','שצירפתי','למעלה','באתר','לאתר','שים','הוסף','תוסיף','שלב','תשלב','image','images','photo','photos','logo','the','this','that','above','earlier','previous','last','first','second','third','add','put','use','site','website']);
const PENDING_KEY_PREFIX = 'codevoyage_image_reference_';

function normalize(value) {
  return String(value || '').toLowerCase().replace(/[\u200B-\u200F\u202A-\u202E]/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

function evidenceText(item) {
  return normalize([item?.fileName, item?.facts?.businessName, item?.facts?.logoText, item?.facts?.address,
    ...(Array.isArray(item?.facts?.services) ? item.facts.services : []),
    ...(Array.isArray(item?.facts?.brandColors) ? item.facts.brandColors : [])].filter(Boolean).join(' '));
}

function imageLabel(item, index) {
  const facts = item?.facts || {};
  const detail = facts.logoText || facts.businessName || item?.fileName || `תמונה ${index + 1}`;
  return `${index + 1}. ${String(detail).replace(/[\r\n]/g, ' ').slice(0, 80)}`;
}

function pendingKey(threadId) { return `${PENDING_KEY_PREFIX}${threadId || 'thread'}`; }
function readPending(threadId) {
  try { return JSON.parse(sessionStorage.getItem(pendingKey(threadId)) || 'null'); } catch { return null; }
}
function clearPending(threadId) { try { sessionStorage.removeItem(pendingKey(threadId)); } catch {} }
function savePending(threadId, ids) {
  try { sessionStorage.setItem(pendingKey(threadId), JSON.stringify({ ids, createdAt: Date.now() })); } catch {}
}

function ordinalIndex(text) {
  const n = normalize(text);
  const numeric = n.match(/(?:תמונה|image|photo)\s*(\d{1,2})(?!\d)/u);
  if (numeric) return Number(numeric[1]) - 1;
  if (/(?:ראשונה|first)(?![\p{L}\p{N}])/u.test(n)) return 0;
  if (/(?:שניה|שנייה|second)(?![\p{L}\p{N}])/u.test(n)) return 1;
  if (/(?:שלישית|third)(?![\p{L}\p{N}])/u.test(n)) return 2;
  return null;
}

function requestedTailCount(text) {
  const n = normalize(text);
  if (/(?:שתי|שני|2)\s+(?:התמונות|תמונות|images|photos).{0,20}(?:האחרונות|אחרונות|last)/u.test(n)) return 2;
  if (/(?:שלוש|3)\s+(?:התמונות|תמונות|images|photos).{0,20}(?:האחרונות|אחרונות|last)/u.test(n)) return 3;
  return null;
}

function imagesAgo(text) {
  const n = normalize(text);
  if (/(?:לפני\s+שתי\s+תמונות|two\s+images?\s+ago)/u.test(n)) return 2;
  if (/(?:לפני\s+שלוש\s+תמונות|three\s+images?\s+ago)/u.test(n)) return 3;
  return null;
}

function followUpOrdinal(text, pendingItems) {
  if (!pendingItems?.length) return null;
  const n = normalize(text);
  if (/^(?:ה?ראשונה|first|1)$/u.test(n)) return pendingItems[0]?.id || null;
  if (/^(?:ה?שניה|ה?שנייה|second|2)$/u.test(n)) return pendingItems[1]?.id || null;
  if (/^(?:ה?שלישית|third|3)$/u.test(n)) return pendingItems[2]?.id || null;
  if (/^(?:האחרונה|last)$/u.test(n)) return pendingItems[pendingItems.length - 1]?.id || null;
  return null;
}

export function hasHistoricalImageReference(task) {
  const text = String(task || '');
  if (!IMAGE_REFERENCE.test(text)) return false;
  // Inspecting or fixing the product's image feature goes to the agent as-is, unless the
  // customer explicitly points at an image they sent in this chat.
  if (IMAGE_FEATURE_WORK.test(text) && !EXPLICIT_CHAT_REFERENCE.test(text)) return false;
  return HISTORICAL_REFERENCE.test(text) || DEFINITE_REFERENCE.test(text);
}

export function hasPendingImageReference(threadId) {
  const pending = readPending(threadId);
  if (!pending?.ids?.length) return false;
  if (Date.now() - Number(pending.createdAt || 0) >= 15 * 60 * 1000) {
    clearPending(threadId);
    return false;
  }
  return true;
}

export function resolveHistoricalImageReferences({ task, evidence = [], threadId = '' }) {
  const items = (Array.isArray(evidence) ? evidence : []).filter(item => item?.id);
  const pending = readPending(threadId);
  if (pending?.ids?.length && Date.now() - Number(pending.createdAt || 0) < 15 * 60 * 1000) {
    const pendingItems = pending.ids.map(id => items.find(item => item.id === id)).filter(Boolean);
    const chosen = followUpOrdinal(task, pendingItems);
    if (chosen) { clearPending(threadId); return { ids: [chosen], clarification: null, reason: 'clarification-follow-up' }; }
  } else if (pending) clearPending(threadId);

  if (!hasHistoricalImageReference(task)) return { ids: [], clarification: null, reason: 'not-a-historical-reference' };
  if (!items.length) {
    // Only an explicit "the image I sent" can be answered locally; anything else reaches the
    // agent, which sees the project and can act on it (never a dead end in the browser).
    if (!EXPLICIT_CHAT_REFERENCE.test(String(task || ''))) return { ids: [], clarification: null, reason: 'no-images-pass-through' };
    return { ids: [], clarification: 'לא מצאתי בשיחה הזו תמונה מאושרת שאפשר לשלב. צרפו את התמונה שוב ואטמיע אותה.', reason: 'no-images' };
  }

  const tailCount = requestedTailCount(task);
  if (tailCount) return { ids: items.slice(-tailCount).map(item => item.id), clarification: null, reason: 'last-n' };

  const ago = imagesAgo(task);
  if (ago) {
    const candidate = items[items.length - 1 - ago];
    if (candidate) return { ids: [candidate.id], clarification: null, reason: 'images-ago' };
  }

  const explicitIndex = ordinalIndex(task);
  if (explicitIndex !== null && items[explicitIndex]) return { ids: [items[explicitIndex].id], clarification: null, reason: 'ordinal' };

  const normalizedTask = normalize(task);
  if (/(?:האחרונה|האחרון|last|previous|הקודמת)(?![\p{L}\p{N}])/u.test(normalizedTask)) {
    return { ids: [items[items.length - 1].id], clarification: null, reason: 'last' };
  }

  // Match a uniquely described historical image (for example "the red Hapoel logo").
  const tokens = normalizedTask.split(' ').filter(token => token.length >= 3 && !GENERIC_WORDS.has(token));
  const scored = items.map(item => ({ item, score: tokens.reduce((sum, token) => sum + (evidenceText(item).includes(token) ? 1 : 0), 0) }))
    .sort((a, b) => b.score - a.score);
  if (scored[0]?.score >= 2 && scored[0].score > (scored[1]?.score || 0)) {
    return { ids: [scored[0].item.id], clarification: null, reason: 'semantic-metadata-match' };
  }

  if (items.length === 1) return { ids: [items[0].id], clarification: null, reason: 'only-image' };

  // "התמונות" / "the images" with no other cue: every recent confirmed image of this thread
  // (the list is already bounded). "התמונה" / "the image": the most recently attached one,
  // which is what a customer means right after attaching it.
  if (PLURAL_DEFINITE.test(task)) return { ids: items.slice(-3).map(item => item.id), clarification: null, reason: 'definite-plural' };
  if (DEFINITE_REFERENCE.test(task) && !HISTORICAL_REFERENCE.test(task)) return { ids: [items[items.length - 1].id], clarification: null, reason: 'definite-most-recent' };

  const options = items.map(imageLabel).join('\n');
  savePending(threadId, items.map(item => item.id));
  return {
    ids: [],
    clarification: `יש בשיחה כמה תמונות מאושרות ואני לא רוצה לנחש איזו מהן התכוונת. כתוב למשל "תמונה 2", "התמונה השנייה" או "התמונה האחרונה":\n${options}`,
    reason: 'ambiguous'
  };
}
